import { useRef, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import { Select } from '../../../shared/components/Select';
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  MerchantContractType,
  CONTRACT_TYPE_LABELS,
  CONTRACT_TEMPLATE_TENANT_ID,
  type ContractTemplate,
} from '../types';

export interface ContractTemplateSubmitValues {
  name: string;
  contentHtml: string;
  documentUrl?: string;
  contractType?: MerchantContractType;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Present when creating a NEW VERSION of an existing template (name is locked). */
  baseTemplate?: ContractTemplate | null;
  onSubmit: (values: ContractTemplateSubmitValues) => Promise<void> | void;
  isSaving: boolean;
}

const CONTRACT_TYPE_OPTIONS = Object.values(MerchantContractType).map((v) => ({
  title: CONTRACT_TYPE_LABELS[v],
  value: v,
}));

// Word/PDF source documents only — the rendered contentHtml is the signed artefact;
// the uploaded file is kept as documentUrl for provenance (backend @IsUrl).
const DOC_ACCEPT = '.doc,.docx,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const FALLBACK_UPLOAD_ERROR = 'Could not upload the document. Please try again.';

/**
 * Same shape as the contract-template mutations' error reader: osprey-api returns
 * class-validator messages as a string[], everything else as a string.
 */
function uploadErrorMessage(err: unknown): string {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || FALLBACK_UPLOAD_ERROR;
  return (typeof msg === 'string' ? msg : '') || FALLBACK_UPLOAD_ERROR;
}

export function ContractTemplateFormModal({
  isOpen,
  onClose,
  baseTemplate,
  onSubmit,
  isSaving,
}: Props) {
  const isVersion = !!baseTemplate;
  const { upload, isUploading } = useMediaUpload();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(baseTemplate?.name ?? '');
  const [contractType, setContractType] = useState<string>(baseTemplate?.contractType ?? '');
  const [contentHtml, setContentHtml] = useState(baseTemplate?.contentHtml ?? '');
  const [documentUrl, setDocumentUrl] = useState<string | undefined>(
    baseTemplate?.documentUrl ?? undefined,
  );
  const [docName, setDocName] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      // Contract source docs are platform-level TCS assets → APP bucket, not a
      // merchant's, and pinned to the platform tenant key so the upload does not
      // depend on a merchant being selected (PE-2286 — see
      // CONTRACT_TEMPLATE_TENANT_ID).
      //
      // DEPENDS ON A BACKEND FIX: `x-tenant-type` is absent from osprey-api's CORS
      // `allowedHeaders` (main.ts) and from ProxyService's forward allowlist
      // (proxy.service.ts), so today the preflight is rejected and the header never
      // reaches media-service. Filed separately; do not work around it here — the
      // frontend request is correct, and the catch below now makes the failure legible.
      const uploaded = await upload(file, 'rack-and-box', {
        tenantId: CONTRACT_TEMPLATE_TENANT_ID,
        tenantType: 'APP',
      });
      setDocumentUrl(uploaded.url);
      setDocName(file.name);
      toast.success('Document uploaded');
    } catch (err) {
      // Never swallow this: the real cause (CORS preflight, missing tenant header,
      // unsupported file type, size cap) only exists in the thrown error, and a
      // silent failure left the admin staring at an unchanged button.
      toast.error('Document upload failed', uploadErrorMessage(err));
    }
  };

  const canSubmit =
    (isVersion || name.trim().length >= 2) && contentHtml.trim().length > 0 && !isSaving && !isUploading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      name: (baseTemplate?.name ?? name).trim(),
      contentHtml,
      documentUrl,
      contractType: contractType ? (contractType as MerchantContractType) : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {isVersion ? `New version — ${baseTemplate?.name}` : 'New contract template'}
            </h2>
            {isVersion && (
              <p className="text-[13px] text-muted-foreground mt-1">
                Creates version {(baseTemplate?.version ?? 0) + 1}. It stays inactive until you activate it.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="space-y-5">
          {!isVersion && (
            <Input
              label="Template name"
              placeholder="e.g. Standard Commission Agreement"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}

          <Select
            label="Contract type (optional)"
            placeholder="Not tied to a contract type"
            options={CONTRACT_TYPE_OPTIONS}
            value={contractType}
            onChange={setContractType}
          />

          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-1">
              Source document (Word / PDF, optional)
            </label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => fileRef.current?.click()}
                loading={isUploading}
                disabled={isUploading}
              >
                {documentUrl ? 'Replace document' : 'Upload document'}
              </Button>
              {docName && <span className="text-[13px] text-muted-foreground truncate">{docName}</span>}
              {!docName && documentUrl && (
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] text-blue-600 hover:underline truncate"
                >
                  View current document
                </a>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={DOC_ACCEPT}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <p className="text-[12px] text-muted-foreground mt-1">
              Kept for provenance. The rendered content below is what merchants sign.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[14px] font-[600] text-foreground">Rendered content (HTML)</label>
              <button
                type="button"
                onClick={() => setShowPreview((p) => !p)}
                className="text-[13px] text-blue-600 hover:underline"
              >
                {showPreview ? 'Edit HTML' : 'Preview'}
              </button>
            </div>
            {showPreview ? (
              // Rendered inside a fully-sandboxed iframe (no scripts / forms / same-origin) so the
              // template HTML is inert and isolated from osprey's origin & session — avoids the XSS
              // surface of dangerouslySetInnerHTML without pulling in a sanitizer dependency.
              <iframe
                title="Contract preview"
                sandbox=""
                srcDoc={contentHtml}
                className="w-full min-h-[220px] border border-border rounded-[12px] bg-white"
              />
            ) : (
              <TextArea
                rows={10}
                placeholder="<h1>Agreement</h1><p>…</p>"
                value={contentHtml}
                onChange={(e) => setContentHtml(e.target.value)}
                className="font-mono text-[13px]"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" size="md" type="button" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button size="md" type="button" onClick={handleSubmit} loading={isSaving} disabled={!canSubmit}>
            {isVersion ? 'Create version' : 'Create template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
